export type AuthPersistence = "session" | "local";

const AUTH_TOKEN_KEY = "oxy_access_token";
const AUTH_LOGOUT_SYNC_KEY = "oxy_auth_logout_sync";

let currentPersistence: AuthPersistence | null = null;

const invalidationListeners = new Set<() => void>();

function isBrowserEnvironment() {
  return typeof window !== "undefined";
}

function getStorage(persistence: AuthPersistence) {
  return persistence === "session" ? window.sessionStorage : window.localStorage;
}

function removeStoredAuthToken() {
  if (!isBrowserEnvironment()) {
    currentPersistence = null;
    return;
  }

  window.sessionStorage.removeItem(AUTH_TOKEN_KEY);
  window.localStorage.removeItem(AUTH_TOKEN_KEY);
  currentPersistence = null;
}

function broadcastLogoutSyncEvent() {
  if (!isBrowserEnvironment()) {
    return;
  }

  window.localStorage.setItem(
    AUTH_LOGOUT_SYNC_KEY,
    JSON.stringify({
      at: Date.now(),
      nonce: Math.random().toString(36).slice(2),
    }),
  );
}

export function readStoredAuthToken(): string | null {
  if (!isBrowserEnvironment()) {
    currentPersistence = null;
    return null;
  }

  const sessionToken = getStorage("session").getItem(AUTH_TOKEN_KEY);

  if (sessionToken) {
    currentPersistence = "session";
    return sessionToken;
  }

  const localToken = getStorage("local").getItem(AUTH_TOKEN_KEY);

  if (localToken) {
    currentPersistence = "local";
    return localToken;
  }

  currentPersistence = null;
  return null;
}

export function persistAuthToken(token: string, rememberMe = false) {
  removeStoredAuthToken();

  if (!isBrowserEnvironment()) {
    return;
  }

  const persistence: AuthPersistence = rememberMe ? "local" : "session";
  getStorage(persistence).setItem(AUTH_TOKEN_KEY, token);
  currentPersistence = persistence;
}

export function registerAuthSessionInvalidationListener(listener: () => void) {
  invalidationListeners.add(listener);

  return () => {
    invalidationListeners.delete(listener);
  };
}

export function invalidateAuthSession(options?: { broadcast?: boolean }) {
  removeStoredAuthToken();

  if (options?.broadcast !== false) {
    broadcastLogoutSyncEvent();
  }

  invalidationListeners.forEach((listener) => {
    listener();
  });
}

export function shouldHandleAuthSyncEvent(event: StorageEvent) {
  if (!isBrowserEnvironment()) {
    return false;
  }

  if (event.storageArea !== window.localStorage) {
    return false;
  }

  if (currentPersistence !== "local") {
    return false;
  }

  return event.key === AUTH_LOGOUT_SYNC_KEY && Boolean(event.newValue);
}
